/**
 * Help content admin: form shapes, conversions and dirty checks.
 */

import { HelpLimits, type HelpPageSaveRequest, type HelpPageView, type HelpTourSaveRequest, type HelpTourStepView, type HelpTourView } from '@actschurch/ccms-sdk';
import type { HelpPageDef } from '@/components/help';

export const PLACEMENT_OPTIONS = [
  { value: 'bottom', label: 'Below the element' },
  { value: 'top', label: 'Above the element' },
  { value: 'right', label: 'Right of the element' },
  { value: 'left', label: 'Left of the element' },
  { value: 'center', label: 'Centre of the screen' },
];

/** The editor toolbar matches the server allow-list (no images, colours, tables or alignment). */
export const HELP_EDITOR_TOOLBAR = {
  bold: true,
  italic: true,
  underline: false,
  strike: false,
  code: true,
  headings: true,
  lists: true,
  blockquote: true,
  codeBlock: false,
  alignment: false,
  textColor: false,
  fontSize: false,
  link: true,
  image: false,
  table: false,
  clearFormatting: true,
  undo: true,
};

/** Empty editor output counts as nothing; whitespace between tags is ignored. */
export function normaliseHtml(html: string | null | undefined): string {
  const s = (html ?? '').replace(/>\s+</g, '><').trim();
  if (!s || /^(<p>(<br\s*\/?>)?<\/p>)+$/i.test(s)) return '';
  return s;
}

/**
 * Canonical form for change detection only: the editor wraps list items in <p>, writes <strong>
 * for <b> and so on. Those rewrites are not changes.
 */
export function canonicalHtml(html: string | null | undefined): string {
  const s = normaliseHtml(html);
  if (!s || typeof DOMParser === 'undefined') return s;
  const doc = new DOMParser().parseFromString(`<div>${s}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return s;
  root.querySelectorAll('b').forEach((el) => {
    const strong = doc.createElement('strong');
    strong.innerHTML = el.innerHTML;
    el.replaceWith(strong);
  });
  root.querySelectorAll('i').forEach((el) => {
    const em = doc.createElement('em');
    em.innerHTML = el.innerHTML;
    el.replaceWith(em);
  });
  root.querySelectorAll('li > p:only-child').forEach((p) => p.replaceWith(...Array.from(p.childNodes)));
  root.querySelectorAll('p').forEach((p) => {
    if (!p.textContent?.trim() && !p.querySelector('br')) p.remove();
  });
  root.querySelectorAll('[rel],[target]').forEach((el) => {
    el.removeAttribute('rel');
    el.removeAttribute('target');
  });
  return root.innerHTML.replace(/\s+/g, ' ').replace(/>\s+</g, '><').trim();
}

export function cleanQuestions(list: string[] | null | undefined, max: number): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of list ?? []) {
    const q = (raw ?? '').trim().slice(0, HelpLimits.maxSuggestedQuestionLength);
    const key = q.toLowerCase();
    if (!q || seen.has(key)) continue;
    seen.add(key);
    out.push(q);
    if (out.length >= max) break;
  }
  return out;
}

// ---------------------------------------------------------------- page form

export interface TabForm {
  tabKey: string;
  title: string;
  bodyHtml: string;
  suggestedQuestions: string[];
  /** In the registry (part of the real UI) or only stored. */
  inRegistry: boolean;
  purpose?: string;
}

export interface PageForm {
  appKey: string;
  title: string;
  route: string;
  menuPath: string;
  accessNote: string;
  sortOrder: number;
  isActive: boolean;
  summaryHtml: string;
  suggestedQuestions: string[];
  tabs: TabForm[];
}

export function pageToForm(pageKey: string, page: HelpPageView | null | undefined, def: HelpPageDef | undefined): PageForm {
  const stored = new Map((page?.tabs ?? []).map((t) => [t.tabKey, t] as const));
  const tabs: TabForm[] = (def?.tabs ?? []).map((t) => {
    const s = stored.get(t.key);
    return {
      tabKey: t.key,
      title: s?.title || t.title,
      bodyHtml: s?.bodyHtml ?? '',
      suggestedQuestions: s?.suggestedQuestions ?? [],
      inRegistry: true,
      purpose: t.purpose,
    };
  });
  for (const s of [...(page?.tabs ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)) {
    if (tabs.some((t) => t.tabKey === s.tabKey)) continue;
    tabs.push({ tabKey: s.tabKey, title: s.title, bodyHtml: s.bodyHtml ?? '', suggestedQuestions: s.suggestedQuestions ?? [], inRegistry: false });
  }
  return {
    appKey: page?.appKey || def?.appKey || pageKey.split('.')[0] || '',
    title: page?.title || def?.title || '',
    route: page?.route ?? def?.route ?? '',
    menuPath: page?.menuPath ?? def?.menuPath ?? '',
    accessNote: page?.accessNote ?? def?.accessNote ?? '',
    sortOrder: page?.sortOrder ?? def?.sortOrder ?? 0,
    isActive: page?.isActive ?? true,
    summaryHtml: page?.summaryHtml ?? '',
    suggestedQuestions: page?.suggestedQuestions ?? [],
    tabs,
  };
}

export function formToSaveRequest(form: PageForm): HelpPageSaveRequest {
  return {
    appKey: form.appKey.trim(),
    title: form.title.trim(),
    route: form.route.trim() || null,
    menuPath: form.menuPath.trim() || null,
    accessNote: form.accessNote.trim() || null,
    sortOrder: Number.isFinite(form.sortOrder) ? form.sortOrder : 0,
    isActive: form.isActive,
    summaryHtml: normaliseHtml(form.summaryHtml) || null,
    suggestedQuestions: cleanQuestions(form.suggestedQuestions, HelpLimits.maxPageSuggestedQuestions),
    tabs: form.tabs.map((t, i) => ({
      tabKey: t.tabKey,
      title: t.title.trim() || t.tabKey,
      bodyHtml: normaliseHtml(t.bodyHtml) || null,
      sortOrder: i + 1,
      suggestedQuestions: cleanQuestions(t.suggestedQuestions, HelpLimits.maxTabSuggestedQuestions),
    })),
  };
}

/** Stable comparison string (HTML in canonical form, so editor rewrites do not count). */
export function pageFormSignature(form: PageForm): string {
  const req = formToSaveRequest(form);
  return JSON.stringify({ ...req, summaryHtml: canonicalHtml(req.summaryHtml), tabs: req.tabs.map((t) => ({ ...t, bodyHtml: canonicalHtml(t.bodyHtml) })) });
}

// ---------------------------------------------------------------- tour form

export interface StepForm {
  /** Local id for React keys. */
  uid: string;
  target: string;
  title: string;
  bodyHtml: string;
  placement: string;
  route: string;
}

export interface TourForm {
  tourKey: string;
  isNew: boolean;
  title: string;
  description: string;
  autoStart: boolean;
  isActive: boolean;
  bumpVersion: boolean;
  version: number;
  steps: StepForm[];
}

let uidSeq = 0;
export const newUid = () => `s${Date.now().toString(36)}${(uidSeq += 1)}`;

export function newStep(): StepForm {
  return { uid: newUid(), target: '', title: '', bodyHtml: '', placement: 'bottom', route: '' };
}

export function tourToForm(tour: HelpTourView): TourForm {
  return {
    tourKey: tour.tourKey,
    isNew: false,
    title: tour.title,
    description: tour.description ?? '',
    autoStart: tour.autoStart,
    isActive: tour.isActive,
    bumpVersion: false,
    version: tour.version,
    steps: [...(tour.steps ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((s) => ({
        uid: newUid(),
        target: s.target ?? '',
        title: s.title,
        bodyHtml: s.bodyHtml ?? '',
        placement: s.placement || 'bottom',
        route: s.route ?? '',
      })),
  };
}

export function newTourForm(pageKey: string, existingKeys: string[]): TourForm {
  let key = `${pageKey}.intro`;
  let n = 2;
  while (existingKeys.includes(key)) {
    key = `${pageKey}.tour-${n}`;
    n += 1;
  }
  return { tourKey: key, isNew: true, title: '', description: '', autoStart: true, isActive: true, bumpVersion: false, version: 1, steps: [newStep()] };
}

export function stepsToView(steps: StepForm[]): HelpTourStepView[] {
  return steps.map((s, i) => ({
    sortOrder: i + 1,
    target: s.placement === 'center' ? null : s.target.trim() || null,
    title: s.title.trim(),
    bodyHtml: normaliseHtml(s.bodyHtml) || null,
    placement: s.target.trim() ? s.placement || 'bottom' : 'center',
    route: s.route.trim() || null,
  }));
}

export function tourFormToSaveRequest(pageKey: string, form: TourForm): HelpTourSaveRequest {
  return {
    pageKey,
    title: form.title.trim(),
    description: form.description.trim() || null,
    autoStart: form.autoStart,
    isActive: form.isActive,
    bumpVersion: form.bumpVersion,
    steps: stepsToView(form.steps),
  };
}

export function tourFormSignature(pageKey: string, form: TourForm): string {
  const req = tourFormToSaveRequest(pageKey, form);
  return JSON.stringify({ key: form.tourKey, ...req, steps: req.steps.map((st) => ({ ...st, bodyHtml: canonicalHtml(st.bodyHtml) })) });
}

/** Tour keys: lower case letters, digits, dots and dashes. */
export const TOUR_KEY_PATTERN = /^[a-z0-9][a-z0-9.-]{2,119}$/;

export function errorText(err: unknown, fallback: string): string {
  const m = (err as { message?: unknown } | null)?.message;
  return typeof m === 'string' && m.trim() && !/JSON|ResponseData/i.test(m) ? m : fallback;
}
